import { useQuery } from 'react-query';
import { useTranslation } from 'react-i18next';
import { useMemo } from 'react';

import { runAction } from '../actions/IntegrationActions';

import pipedriveLogo from './integrations/pipedrive.svg';
import hubspotLogo from './integrations/hubspot.svg';
import slackLogo from './integrations/slack.svg';
import googleDriveLogo from './integrations/google-drive.svg';

// eslint-disable-next-line import/prefer-default-export
export function useIntegrationMetadata() {
  const { t } = useTranslation();

  return {
    pipedrive: {
      name: 'Pipedrive',
      description: t(
        'Få dina summeringar direkt i Pipedrive och ge vår AI möjlighet att använda din data'
      ),
      logo: pipedriveLogo,
      scopes: [t('Läsa och skriva till din data i Pipedrive')],
      hasSettings: false,
    },
    hubspot: {
      name: 'HubSpot',
      description: t(
        'Få dina summeringar direkt i HubSpot och ge vår AI möjlighet att använda din data'
      ),
      logo: hubspotLogo,
      scopes: [t('Läsa och skriva till din data i HubSpot')],
      hasSettings: false,
    },
    slack: {
      name: 'Slack',
      description: t('Skapa automatiseringar som skriver direkt till Slack'),
      logo: slackLogo,
      scopes: [t('Läsa vilka publika kanaler som finns'), t('Skriva till kanaler'), t('Läsa vilka användare som finns'), t('Skriva till användare')],
      hasSettings: false,
    },
    googleDrive: {
      name: 'Google Drive',
      description: t('Anslut Google Drive direkt till Klang'),
      logo: googleDriveLogo,
      scopes: [t('Läsa vilka mappar och filer som finns'), t('Skapa filer')],
      hasSettings: false,
    },
  };
}

export async function getSlackChannels() {
  let nextCursor = null;

  let channels = [];

  while (true) {
    const response = await runAction('slack', 'conversations.list', {
      cursor: nextCursor,
    });

    channels = channels.concat(response.channels);

    if (
      !response.response_metadata ||
      !response.response_metadata.next_cursor
    ) {
      break;
    }
    nextCursor = response.response_metadata.next_cursor;
  }

  return channels
    .map(({ id, name }) => ({ id, name }))
    .sort((a, b) => a.name.localeCompare(b.name));
}

export function useSlackChannels() {
  return useQuery('slackChannels', getSlackChannels, {
    staleTime: 1000 * 60 * 2,
    cacheTime: 1000 * 60 * 5,
    retry: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    refetchOnMount: false,
    refetchInterval: false,
    refetchIntervalInBackground: false,
  });
}

export async function getSlackUsers() {
  let nextCursor = null;
  let users = [];

  while (true) {
    const response = await runAction('slack', 'users.list', {
      cursor: nextCursor,
    });

    users = users.concat(response.members);

    if (
      !response.response_metadata ||
      !response.response_metadata.next_cursor
    ) {
      break;
    }
    nextCursor = response.response_metadata.next_cursor;
  }

  return users
    .filter(member => !member.is_bot && !member.deleted)
    .map(({ id, name, real_name }) => ({
      id,
      name,
      real_name: real_name || name
    }))
    .sort((a, b) => a.real_name.localeCompare(b.real_name));
}

export function useSlackUsers() {
  return useQuery('slackUsers', getSlackUsers, {
    staleTime: 1000 * 60 * 2,
    cacheTime: 1000 * 60 * 5,
    retry: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    refetchOnMount: false,
    refetchInterval: false,
    refetchIntervalInBackground: false,
  });
}

export function useSlackDestinations() {
  const { data: channels, isLoading: channelsLoading } = useSlackChannels();
  const { data: users, isLoading: usersLoading } = useSlackUsers();

  const data = useMemo(() => {
    return [
      ...(users || []).map((user) => ({ type: 'user', ...user })),
      ...(channels || []).map((channel) => ({ type: 'channel', ...channel })),
    ];
  }, [channels, users]);

  return {
    data,
    isLoading: channelsLoading || usersLoading,
  };
}
